import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";

interface SocketContextType {
  socket: Socket | null;
  paymentSocket: Socket | null;
  connected: boolean;
  paymentConnected: boolean;
  userSecretKey: string;
  paymentSecretKey: string;
  subscribed: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  paymentSocket: null,
  connected: false,
  paymentConnected: false,
  userSecretKey: "",
  paymentSecretKey: "",
  subscribed: false,
});

export const useSocket = () => useContext(SocketContext);

interface Props {
  children: ReactNode;
  secretKey: string;
  paymentSecretKey: string;
}

const BASE_URL = (process.env.REACT_APP_BASE_URL_WS || "").trim();

export const SocketProvider = ({
  children,
  secretKey,
  paymentSecretKey,
}: Props) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [paymentSocket, setPaymentSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [paymentConnected, setPaymentConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const paymentSocketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const trimmedSecret = secretKey.trim();
    if (!trimmedSecret) {
      console.warn(
        "Socket secretKey is empty. Set REACT_APP_RELEASE_ROOM_SECRET_KEY."
      );
      return;
    }

    if (!socketRef.current) {
      if (!BASE_URL) {
        console.warn("Socket base URL is empty. Set REACT_APP_BASE_URL_WS.");
      }

      console.log("Connecting release-room socket", {
        baseUrl: BASE_URL,
        namespace: "/ws/release-room",
        secretKeyPreview: `${trimmedSecret.slice(0, 4)}****`,
      });

      const releaseRoomSocket = io(`${BASE_URL}/ws/release-room`, {
        query: { secretKey: trimmedSecret },
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
        withCredentials: true,
      });

      socketRef.current = releaseRoomSocket;
      setSocket(releaseRoomSocket);

      releaseRoomSocket.on("connect", () => {
        console.log("Release-room socket connected:", releaseRoomSocket.id);
        setConnected(true);
        releaseRoomSocket.emit("subscribeToReleaseRoomStatus");
        setSubscribed(true);
      });

      releaseRoomSocket.on("disconnect", () => {
        console.log("Release-room socket disconnected");
        setConnected(false);
        setSubscribed(false);
      });

      releaseRoomSocket.on("connect_error", (err) => {
        console.error(
          "Release-room socket connect_error:",
          err?.message || err
        );
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
      setConnected(false);
      setSubscribed(false);
    };
  }, [secretKey]);

  useEffect(() => {
    const trimmedPaymentSecret = paymentSecretKey.trim();

    if (!trimmedPaymentSecret) {
      console.warn(
        "Payment socket not started. Set REACT_APP_KIOSK_SECRET_KEY in .env (required for ticket extension redirect)."
      );
      return;
    }

    if (!BASE_URL) {
      console.warn("Socket base URL is empty. Set REACT_APP_BASE_URL_WS.");
      return;
    }

    if (paymentSocketRef.current) return;

    console.log("Connecting payment socket", {
      baseUrl: BASE_URL,
      namespace: "/ws/payment",
      secretKeyPreview: `${trimmedPaymentSecret.slice(0, 4)}****`,
    });

    const payment = io(`${BASE_URL}/ws/payment`, {
      query: { secretKey: trimmedPaymentSecret },
      transports: ["websocket"],
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      withCredentials: true,
    });

    paymentSocketRef.current = payment;
    setPaymentSocket(payment);

    payment.on("connect", () => {
      console.log("Payment socket connected:", payment.id);
      setPaymentConnected(true);
    });

    payment.on("disconnect", (reason) => {
      console.log("Payment socket disconnected:", reason);
      setPaymentConnected(false);
    });

    payment.on("connect_error", (err) => {
      console.error("Payment socket connect_error:", err?.message || err);
      setPaymentConnected(false);
    });

    payment.on("error", (err) => {
      console.error("Payment socket error:", err);
    });

    return () => {
      if (paymentSocketRef.current) {
        paymentSocketRef.current.removeAllListeners();
        paymentSocketRef.current.disconnect();
        paymentSocketRef.current = null;
      }
      setPaymentSocket(null);
      setPaymentConnected(false);
    };
  }, [paymentSecretKey]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        paymentSocket,
        connected,
        paymentConnected,
        userSecretKey: secretKey,
        paymentSecretKey,
        subscribed,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
